import { WorkspacePageLayout } from "../../components/PageLayout/WorkspacePageLayout";
import { useAppConfig, useWorkspace, useWorkspaceRole } from "../../lib/context";
import { DestinationConfig, ServiceConfig, StreamConfig } from "../../lib/schema";
import { branding } from "../../lib/branding";
import { Badge, Button, Checkbox, Select, Tooltip } from "antd";
import React, { ReactNode, useEffect, useState } from "react";
import { FaGlobe } from "react-icons/fa";
import { coreDestinationsMap } from "../../lib/schema/destinations";
import { useTitle } from "../../lib/ui";
import { z } from "zod";
import { ConfigurationObjectLinkDbModel } from "../../prisma/schema";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { ProvisionDatabaseButton } from "../../components/ProvisionDatabaseButton/ProvisionDatabaseButton";
import { ConnectionsDiagram } from "../../components/ConnectionsDiagram/ConnectionsDiagram";
import { getLog } from "juava";
import { Activity, ArrowDown, ArrowUp, Chrome, Edit3, MoreVertical, Share2, Wrench, Zap } from "lucide-react";
import { EnvironmentTag, DirectionTag, DeliveryModeTag } from "../../components/CdpTag";
import classNames from "classnames";
import { toURL } from "../../lib/shared/url";
import JSON5 from "json5";
import { ButtonGroup } from "../../components/ButtonGroup/ButtonGroup";
import { useConfigObjectLinks, useConfigObjectList } from "../../lib/store";
import Link from "next/link";
import { WorkspacePermissionsType } from "../../lib/workspace-roles";

function HoverBorder({ children, forceHover }: { children: ReactNode; forceHover?: boolean }) {
  const [_hover, setHover] = useState(false);
  const hover = forceHover || _hover;
  return (
    <div
      className={classNames(
        "border transition duration-150 rounded-lg w-full",
        hover ? "border-primary" : "border-primaryLighter"
      )}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={hover ? { padding: "0px", borderWidth: "3px" } : { padding: "2px", borderWidth: "1px" }}
    >
      {children}
    </div>
  );
}

function ConditionalBadge({ icon, tooltip, children }: { icon?: ReactNode; tooltip?: ReactNode; children: ReactNode }) {
  if (!icon) {
    return <>{children}</>;
  }
  return (
    <Badge className="w-full block min-w-full" count={tooltip ? <Tooltip title={tooltip}>{icon}</Tooltip> : icon}>
      {children}
    </Badge>
  );
}

function Card({
  title,
  configLink,
  icon,
  selected,
  badge,
  actions,
  environmentTag,
  directionTag,
  deliveryModeTag,
}: {
  title: string;
  configLink?: string;
  icon: ReactNode;
  actions?: { label: ReactNode; icon?: ReactNode; href: string; requiredPermission?: WorkspacePermissionsType }[];
  selected?: boolean;
  badge?: {
    icon: ReactNode;
    tooltip?: ReactNode;
  };
  environmentTag?: ReactNode;
  directionTag?: ReactNode;
  deliveryModeTag?: ReactNode;
}) {
  const card = (
    <ConditionalBadge icon={badge?.icon} tooltip={badge?.tooltip}>
      <HoverBorder forceHover={selected}>
        <div className={classNames(`w-full px-4 py-5 rounded-lg text-primary`)}>
          <div className="flex flex-nowrap justify-between items-start">
            <div className="flex flex-start items-start space-x-4">
              <div className="w-6 h-6 mt-1 shrink-0">{icon}</div>
              <div className="flex flex-col gap-1.5">
                <p className="text-lg py-0 text-textDark break-words">{title}</p>
                {(environmentTag || directionTag || deliveryModeTag) && (
                  <div className="flex flex-wrap gap-1">
                    {environmentTag}
                    {directionTag}
                    {deliveryModeTag}
                  </div>
                )}
              </div>
            </div>
            {actions && actions.length > 0 && (
              <ButtonGroup
                dotsButtonProps={{
                  size: "small",
                  ghost: true,
                  icon: <MoreVertical strokeWidth={2} className="text-textLight w-5 h-5" />,
                }}
                items={actions.map(a => ({ ...a, collapsed: true }))}
              />
            )}
          </div>
        </div>
      </HoverBorder>
    </ConditionalBadge>
  );
  return configLink ? <Link href={configLink}>{card}</Link> : card;
}

type ConfigurationLinkDbModel = Omit<z.infer<typeof ConfigurationObjectLinkDbModel>, "data"> & {
  data?: { enabled?: boolean; [key: string]: any };
};

function DestinationCard({
  dest,
  selected,
  links,
}: {
  dest: DestinationConfig;
  selected?: boolean;
  links?: ConfigurationLinkDbModel[];
}) {
  const workspace = useWorkspace();
  const destLinks = links?.filter(l => l.toId === dest.id) ?? [];
  const hasConnection = destLinks.length > 0;
  const allDisabled = hasConnection && destLinks.every(l => (l.data as any)?.enabled === false);
  const badge = allDisabled
    ? {
        icon: (
          <div className="bg-warning w-5 h-5 rounded-full flex items-center justify-center font-bold text-text">!</div>
        ),
        tooltip: "All connections to this destination are disabled",
      }
    : undefined;
  const isDeviceMode = Boolean(coreDestinationsMap[dest.destinationType]?.deviceOptions);
  const card = (
    <Card
      selected={selected}
      icon={coreDestinationsMap[dest.destinationType]?.icon || <FaGlobe className="w-full h-full" />}
      title={dest.name}
      environmentTag={<EnvironmentTag environment={dest.environment} />}
      directionTag={<DirectionTag direction={dest.direction} />}
      deliveryModeTag={
        <DeliveryModeTag isDeviceMode={isDeviceMode} id={`destination-delivery-mode-badge-${dest.id}`} />
      }
      badge={badge}
      actions={
        dest.provisioned
          ? []
          : [
              {
                label: "Edit",
                href: `/destinations?id=${dest.id}`,
                icon: <Edit3 className="w-4 h-4" />,
                requiredPermission: "editEntities",
              },
              {
                label: "View Connected Streams",
                icon: <Zap className="w-4 h-4" />,
                href: `/connections?sorting=${encodeURIComponent(
                  btoa(JSON.stringify({ columns: [{ field: "Source", order: "ascend" }] }))
                )}&destination=${encodeURIComponent(dest.id)}`,
              },
              {
                label: "View Syncs",
                icon: <Share2 className="w-4 h-4" />,
                href: `/syncs?destination=${encodeURIComponent(dest.id)}`,
              },
            ]
      }
      configLink={
        !dest.provisioned ? `/${workspace.slugOrId}/destinations?id=${dest.id}` : `/${workspace.slugOrId}/destinations`
      }
    />
  );
  return (
    <div className="" key={dest.id}>
      {dest.provisioned ? (
        <Badge.Ribbon
          text={
            <>
              <Tooltip
                title={
                  <>
                    This destination is <b>provisioned</b>. It's hosted and managed by Jitsu, so you can't edit
                    connection details. But you can use it as destination and query the data
                  </>
                }
              >
                Provisioned <QuestionCircleOutlined />
              </Tooltip>
            </>
          }
        >
          {card}
        </Badge.Ribbon>
      ) : (
        card
      )}
    </div>
  );
}

function looksLikeUrl(potentialUrl: string) {
  try {
    new URL(potentialUrl);
    return true;
  } catch (e) {
    return false;
  }
}

export const FaviconLoader: React.FC<{ potentialUrl?: string }> = ({ potentialUrl }) => {
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      if (potentialUrl && looksLikeUrl(potentialUrl)) {
        let url;
        let fullUrl =
          potentialUrl.indexOf("http") === 0 ? potentialUrl + "/favicon.ico" : `https://${potentialUrl}/favicon.ico`;
        try {
          url = new URL(fullUrl);
        } catch (e) {
          getLog().atDebug().withCause(e).log(`Failed to parse url ${fullUrl}`);
        }
        let response;
        try {
          response = await fetch(fullUrl);
        } catch (e) {
          getLog().atDebug().withCause(e).log(`Failed to parse url ${fullUrl}`);
          return;
        }
        if (response.ok) {
          setFaviconUrl(fullUrl);
        } else {
          getLog().atDebug().log(`Failed to load favicon for ${fullUrl}`);
        }
      }
    })();
  }, [potentialUrl]);
  return faviconUrl ? (
    <img alt={""} src={faviconUrl} className="w-full h-full" />
  ) : (
    <Chrome className="w-full h-full text-blue-600" />
  );
};

function WorkspaceOverview(props: {
  streams: StreamConfig[];
  destinations: DestinationConfig[];
  connectors: ServiceConfig[];
  links: ConfigurationLinkDbModel[];
}) {
  const appConfig = useAppConfig();
  const workspace = useWorkspace();
  const { destinations, streams, links, connectors } = props;

  // Sort/group state
  const [siteSortDir, setSiteSortDir] = useState<"asc" | "desc">("asc");
  const [destSortDir, setDestSortDir] = useState<"asc" | "desc">("asc");
  const [destGrouped, setDestGrouped] = useState(false);

  // Filter state for sites
  const [environmentFilter, setEnvironmentFilter] = useState<string | undefined>(undefined);
  const [directionFilter, setDirectionFilter] = useState<string | undefined>(undefined);

  // Filter and sort streams
  const filteredStreams = streams.filter(s => {
    const matchesEnvironment = !environmentFilter || s.environment === environmentFilter;
    const matchesDirection = !directionFilter || s.direction === directionFilter;
    return matchesEnvironment && matchesDirection;
  });
  const sortedStreams = [...filteredStreams].sort((a, b) => {
    const cmp = (a.name || "").localeCompare(b.name || "");
    return siteSortDir === "asc" ? cmp : -cmp;
  });

  // Filter state for destinations
  const [destEnvironmentFilter, setDestEnvironmentFilter] = useState<string | undefined>(undefined);
  const [destDirectionFilter, setDestDirectionFilter] = useState<string | undefined>(undefined);

  // Filter destinations based on environment and direction
  const filteredDestinations = destinations.filter(d => {
    const matchesEnvironment = !destEnvironmentFilter || d.environment === destEnvironmentFilter;
    const matchesDirection = !destDirectionFilter || d.direction === destDirectionFilter;
    return matchesEnvironment && matchesDirection;
  });

  // Sort by name; when grouped, reorder so destinations of the same type are
  // contiguous — group order is determined by the first occurrence of each type
  // in the name-sorted list (matching hub-frontend's sortDestinationsForGrouping).
  const nameSortedDestinations = [...filteredDestinations].sort((a, b) => {
    const cmp = (a.name || "").localeCompare(b.name || "");
    return destSortDir === "asc" ? cmp : -cmp;
  });

  const sortedDestinations = (() => {
    if (!destGrouped) return nameSortedDestinations;
    const typeOrder: string[] = [];
    nameSortedDestinations.forEach(d => {
      if (!typeOrder.includes(d.destinationType)) typeOrder.push(d.destinationType);
    });
    return typeOrder.flatMap(type => nameSortedDestinations.filter(d => d.destinationType === type));
  })();

  const { createEntities } = useWorkspaceRole();
  useTitle(`${branding.productName} : ${workspace.name}`);

  // Site filter bar component
  const siteFilterBar = (
    <div id="site-filter-bar" className="flex flex-row gap-3 items-end flex-wrap">
      <div className="flex flex-col gap-1">
        <span className="text-sm text-textLight">Environment</span>
        <Select
          id="overview-site-environment"
          placeholder="All"
          value={environmentFilter}
          onChange={setEnvironmentFilter}
          style={{ width: 120 }}
          allowClear
          options={[
            { label: "Production", value: "production" },
            { label: "Staging", value: "staging" },
          ]}
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-sm text-textLight">Direction</span>
        <Select
          id="overview-site-direction"
          placeholder="All"
          value={directionFilter}
          onChange={setDirectionFilter}
          style={{ width: 120 }}
          allowClear
          options={[
            { label: "Front-end", value: "front-end" },
            { label: "Back-end", value: "back-end" },
          ]}
        />
      </div>
      <Tooltip title={siteSortDir === "asc" ? "Sort Z–A" : "Sort A–Z"}>
        <Button
          id="site-sort-toggle"
          size="small"
          className="mb-1"
          icon={siteSortDir === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
          onClick={() => setSiteSortDir(d => (d === "asc" ? "desc" : "asc"))}
        />
      </Tooltip>
      <button
        id="site-clear-all"
        className={`text-sm underline pb-1 ${
          environmentFilter || directionFilter
            ? "text-primary hover:text-primaryDark cursor-pointer"
            : "text-textDisabled cursor-default"
        }`}
        onClick={() => {
          setEnvironmentFilter(undefined);
          setDirectionFilter(undefined);
        }}
        disabled={!environmentFilter && !directionFilter}
      >
        Clear
      </button>
    </div>
  );

  // Destination filter bar component
  const destFilterBar = (
    <div id="dest-filter-bar" className="flex flex-row gap-3 items-end flex-wrap">
      <div className="flex flex-col gap-1">
        <span className="text-sm text-textLight">Environment</span>
        <Select
          id="overview-dest-environment"
          placeholder="All"
          value={destEnvironmentFilter}
          onChange={setDestEnvironmentFilter}
          style={{ width: 120 }}
          allowClear
          options={[
            { label: "Production", value: "production" },
            { label: "Staging", value: "staging" },
          ]}
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-sm text-textLight">Direction</span>
        <Select
          id="overview-dest-direction"
          placeholder="All"
          value={destDirectionFilter}
          onChange={setDestDirectionFilter}
          style={{ width: 120 }}
          allowClear
          options={[
            { label: "Front-end", value: "front-end" },
            { label: "Back-end", value: "back-end" },
          ]}
        />
      </div>
      <Checkbox
        id="dest-group-toggle"
        checked={destGrouped}
        onChange={e => setDestGrouped(e.target.checked)}
        className="pb-1"
      >
        Group
      </Checkbox>
      <Tooltip title={destSortDir === "asc" ? "Sort Z–A" : "Sort A–Z"}>
        <Button
          id="dest-sort-toggle"
          size="small"
          className="mb-1"
          icon={destSortDir === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
          onClick={() => setDestSortDir(d => (d === "asc" ? "desc" : "asc"))}
        />
      </Tooltip>
      <button
        id="dest-clear-all"
        className={`text-sm underline pb-1 ${
          destEnvironmentFilter || destDirectionFilter
            ? "text-primary hover:text-primaryDark cursor-pointer"
            : "text-textDisabled cursor-default"
        }`}
        onClick={() => {
          setDestEnvironmentFilter(undefined);
          setDestDirectionFilter(undefined);
        }}
        disabled={!destEnvironmentFilter && !destDirectionFilter}
      >
        Clear
      </button>
    </div>
  );

  return (
    <div>
      {
        <ConnectionsDiagram
          srcFilterBar={siteFilterBar}
          dstFilterBar={destFilterBar}
          srcTotalCount={streams.length}
          dstTotalCount={destinations.length}
          connectorSourcesActions={{
            title: "Connectors",
            newLink: createEntities ? `/services?showCatalog=true` : undefined,
            editLink: `/services`,
            newLinkTooltip: !createEntities ? "You don't have permission to create connectors" : undefined,
          }}
          connectorSources={connectors.map(({ id, name, ...cfg }) => ({
            id,
            card: (forceSelect: boolean) => (
              <Card
                selected={forceSelect}
                icon={
                  <img
                    alt="logo"
                    src={`/api/sources/logo?package=${encodeURIComponent(cfg.package)}&protocol=${cfg.protocol}`}
                  />
                }
                badge={(() => {
                  const connectorLinks = links?.filter(l => l.fromId === id) ?? [];
                  const hasConnection = connectorLinks.length > 0;
                  const allDisabled = hasConnection && connectorLinks.every(l => (l.data as any)?.enabled === false);
                  if (!hasConnection) {
                    return {
                      icon: (
                        <div className="bg-warning w-5 h-5 rounded-full flex items-center justify-center font-bold text-text">
                          !
                        </div>
                      ),
                      tooltip:
                        "The source is not connected to any destination. Connect it to any destination to start seeing the data",
                    };
                  }
                  if (allDisabled) {
                    return {
                      icon: (
                        <div className="bg-warning w-5 h-5 rounded-full flex items-center justify-center font-bold text-text">
                          !
                        </div>
                      ),
                      tooltip: "All connections from this connector are disabled",
                    };
                  }
                  return undefined;
                })()}
                title={name || id}
                configLink={`/${workspace.slugOrId}/services?id=${id}`}
                actions={[
                  {
                    label: "Edit",
                    href: `/services?id=${id}`,
                    icon: <Edit3 className={"w-4 h-4"} />,
                    requiredPermission: "editEntities",
                  },
                  {
                    label: "View Syncs",
                    icon: <Share2 className="w-4 h-4" />,
                    href: `/syncs?source=${encodeURIComponent(id)}`,
                  },
                ]}
              />
            ),
          }))}
          srcActions={{
            title: "Sites",
            newLink: createEntities ? `/streams?id=new` : undefined,
            editLink: `/streams`,
            newLinkTooltip: !createEntities ? "You don't have permission to create sites" : undefined,
          }}
          dstActions={{
            title: "Destinations",
            newLink: createEntities ? `/destinations?showCatalog=true` : undefined,
            editLink: `/destinations`,
            newLinkTooltip: !createEntities ? "You don't have permission to create destinations" : undefined,
          }}
          sources={sortedStreams.map(stream => ({
            id: stream.id,
            card: (forceSelect: boolean) => (
              <Card
                selected={forceSelect}
                icon={<FaviconLoader potentialUrl={stream.name} />}
                title={stream.name || stream.id}
                environmentTag={<EnvironmentTag environment={stream.environment} />}
                directionTag={<DirectionTag direction={stream.direction} />}
                badge={(() => {
                  const sourceLinks = links.filter(l => l.fromId === stream.id);
                  const hasConnection = sourceLinks.length > 0;
                  const allDisabled = hasConnection && sourceLinks.every(l => (l.data as any)?.enabled === false);
                  if (!hasConnection) {
                    return {
                      icon: (
                        <div className="bg-warning w-5 h-5 rounded-full flex items-center justify-center font-bold text-text">
                          !
                        </div>
                      ),
                      tooltip:
                        "The source is not connected to any destination. Connect it to any destination to start seeing the data",
                    };
                  }
                  if (allDisabled) {
                    return {
                      icon: (
                        <div className="bg-warning w-5 h-5 rounded-full flex items-center justify-center font-bold text-text">
                          !
                        </div>
                      ),
                      tooltip: "All connections from this source are disabled",
                    };
                  }
                  return undefined;
                })()}
                configLink={`/${workspace.slugOrId}/streams?id=${stream.id}`}
                actions={[
                  {
                    label: "Edit",
                    href: `/streams?id=${stream.id}`,
                    icon: <Edit3 className={"w-4 h-4"} />,
                    requiredPermission: "editEntities",
                  },
                  {
                    label: "Live Events",
                    icon: <Activity className="w-4 h-4" />,
                    href: toURL("/data", {
                      query: JSON5.stringify({
                        activeView: "incoming",
                        viewState: { incoming: { actorId: stream.id } },
                      }),
                    }),
                  },
                  {
                    label: "View Connected Destinations",
                    icon: <Zap className="w-4 h-4" />,
                    href: `/connections?sorting=${encodeURIComponent(
                      btoa(JSON.stringify({ columns: [{ field: "Source", order: "ascend" }] }))
                    )}&source=${encodeURIComponent(stream.id)}`,
                  },
                  {
                    label: "Setup Instructions",
                    icon: <Wrench className="w-4 h-4" />,
                    href: `/streams?id=${stream.id}&implementationFor=${stream.id}`,
                  },
                ]}
              />
            ),
          }))}
          destinations={sortedDestinations.map(d => ({
            id: d.id,
            group: destGrouped ? coreDestinationsMap[d.destinationType]?.title ?? d.destinationType : undefined,
            card: (forceSelect: boolean) => <DestinationCard selected={forceSelect} dest={d} links={links} />,
          }))}
          connections={links.map(l => ({
            from: l.fromId,
            to: l.toId,
            enabled: (l.data as any)?.enabled !== false,
          }))}
        />
      }
      {appConfig.ee?.available && (
        <div className="flex justify-center">
          <ProvisionDatabaseButton />
        </div>
      )}
    </div>
  );
}

function WorkspaceOverviewLoader() {
  const streams = useConfigObjectList("stream");
  const destinations = useConfigObjectList("destination");
  const services = useConfigObjectList("service");
  const links = useConfigObjectLinks();

  return <WorkspaceOverview streams={streams} destinations={destinations} links={links} connectors={services} />;
}

const WorkspaceOverviewPage = () => {
  return (
    <WorkspacePageLayout>
      <WorkspaceOverviewLoader />
    </WorkspacePageLayout>
  );
};
export default WorkspaceOverviewPage;
